import { createPortal } from "react-dom";

/** Floating top-of-page status toast. Two variants:
 *    - success → green pill with check glyph (matches Overview's
 *      "Dashboard updated" toast)
 *    - error   → red pill with × glyph
 *
 *  Optional `action`: a button rendered to the right of the message,
 *  typically used for "Undo" on bulk operations. The toast stops
 *  being a passive `pointer-events: none` element when an action is
 *  present, so the button is clickable.
 *
 *  Auto-dismiss is the caller's responsibility (setTimeout +
 *  setMessage(null)). */
export function StatusToast({
  message,
  variant = "success",
  action,
}: {
  message: string;
  variant?: "success" | "error";
  action?: { label: string; onClick: () => void };
}) {
  const palette =
    variant === "error"
      ? {
          bg: "rgba(239,68,68,0.96)",
          shadow: "0 8px 24px rgba(239,68,68,0.35), 0 1px 3px rgba(0,0,0,0.2)",
          glyph: "✕",
        }
      : {
          bg: "rgba(34,197,94,0.96)",
          shadow: "0 8px 24px rgba(34,197,94,0.35), 0 1px 3px rgba(0,0,0,0.2)",
          glyph: "✓",
        };
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        padding: "10px 14px 10px 16px",
        borderRadius: 999,
        background: palette.bg,
        color: "#fff",
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: 0.1,
        boxShadow: palette.shadow,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        animation: "toast-in 0.24s ease-out",
        // pointer-events stays passive when there's no action so the
        // toast doesn't block clicks underneath; flip to auto when an
        // Undo button is present.
        pointerEvents: action ? "auto" : "none",
      }}
    >
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
        {palette.glyph}
      </span>
      <span>{message}</span>
      {action && (
        <button
          onClick={action.onClick}
          type="button"
          style={{
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.32)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.2,
            padding: "4px 10px",
            borderRadius: 999,
            cursor: "pointer",
            marginLeft: 4,
          }}
        >
          {action.label}
        </button>
      )}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translate(-50%, -6px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>,
    document.body
  );
}
